/**
 * 4-step progress indicator for the ads wizard. The active step is
 * highlighted; the steps before the active one are marked "done".
 * Clicking a completed step (or the active step) is a no-op — the
 * parent controls navigation explicitly to enforce the "validate
 * metadata" modal before advancing past Step 1.
 *
 * After Step 4 the wizard resets — there is no in-page "review"
 * surface; live progress and final results live on the History /
 * Live Scans pages instead so a page reload never resurrects the
 * last submitted job.
 */

interface Props {
  step: 1 | 2 | 3 | 4;
  labels?: [string, string, string, string];
}

const DEFAULT_LABELS: [string, string, string, string] = [
  'Context',
  'Information',
  'Design',
  'Generate',
];

export function Stepper({ step, labels = DEFAULT_LABELS }: Props) {
  return (
    <ol className="flex items-center justify-between gap-2 bg-white rounded-2xl border border-slate-200 shadow-soft p-3">
      {labels.map((label, idx) => {
        const n = (idx + 1) as 1 | 2 | 3 | 4;
        const isActive = n === step;
        const isDone = n < step;
        return (
          <li
            key={label}
            className={`flex-1 flex items-center gap-2 ${
              idx === labels.length - 1 ? '' : 'border-r border-slate-100 pr-2'
            }`}
          >
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium border ${
                isActive
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : isDone
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-white text-slate-500 border-slate-200'
              }`}
            >
              {isDone ? (
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                n
              )}
            </div>
            <div
              className={`text-sm font-medium ${
                isActive
                  ? 'text-slate-900'
                  : isDone
                    ? 'text-emerald-700'
                    : 'text-slate-500'
              }`}
            >
              {label}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
